import { Link } from "react-router-dom";
import { Star, ArrowRight, Sparkles, HeartHandshake, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { CLINIC } from "../../data/content";
import EnquiriesTicker from "./EnquiriesTicker";

const HERO_IMG = "/hero-amit.jpg";

// Motion props hoisted out of render to avoid recreating identity on every render
const LEFT_ANIM = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};
const RIGHT_ANIM = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.6 },
};
const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

export default function HomeHero() {
  return (
    <div className="container-wide pt-14 md:pt-20 pb-16 grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-6">
        <motion.div {...LEFT_ANIM}>
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream text-brand-blue px-3 py-1 text-xs font-medium">
            <Sparkles size={12} /> Nangloi, West Delhi
          </div>
          <h1 className="mt-5 font-heading text-5xl md:text-6xl leading-[1.02] tracking-tight">
            Move Better. Recover Stronger. <span className="text-brand-amber">Return</span> to the Life You Love.
          </h1>
          <p className="mt-5 text-lg text-brand-ink2 max-w-xl">
            Personalised physiotherapy and rehabilitation for pain, injuries, neurological conditions and post-surgical recovery — built around your condition, your goals and your journey.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" data-testid="hero-book" className="btn-primary">
              Book Your Assessment <ArrowRight size={16} />
            </Link>
            <a
              href={`https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent("Hello Physio Hyve, I'd like to enquire about physiotherapy.")}`}
              target="_blank" rel="noreferrer"
              data-testid="hero-whatsapp"
              className="btn-outline"
            >
              <MessageCircle size={16} /> WhatsApp Our Clinic
            </a>
          </div>
          <div className="mt-7 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              {STAR_KEYS.map((k) => (
                <Star key={k} size={16} className="fill-brand-amber text-brand-amber" />
              ))}
            </div>
            <span className="text-sm text-brand-ink2">
              <span className="font-semibold text-brand-ink">{CLINIC.rating}</span> Google Rating · {CLINIC.reviewsCount} patient reviews
            </span>
            <EnquiriesTicker />
          </div>
        </motion.div>
      </div>

      <div className="md:col-span-6 relative">
        <motion.div
          {...RIGHT_ANIM}
          className="relative aspect-[4/5] md:aspect-[5/6] rounded-[2rem] overflow-hidden bg-brand-cream"
        >
          <img src={HERO_IMG} alt="Dr. Amit Sharma performing dry-needling treatment at Physio Hyve Clinic, Nangloi" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center">
                <HeartHandshake size={18} />
              </div>
              <div>
                <div className="text-xs text-brand-ink2">First step</div>
                <div className="text-sm font-medium text-brand-ink">Personalised assessment before any treatment</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
